import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuthStore } from '../store/auth.store.js';
import { BackgroundEffects } from '../components/lobby/BackgroundEffects.js';
import { Accordion } from '../components/help/Accordion.js';
import { PatternExample } from '../components/help/PatternExample.js';
import { SupportSection } from '../components/help/SupportSection.js';

export function HelpScreen() {
  const { user, isSessionChecked } = useAuthStore();
  const navigate = useNavigate();

  // Route protection
  useEffect(() => {
    if (isSessionChecked && !user) {
      navigate('/login');
    }
  }, [user, isSessionChecked, navigate]);

  const handleBack = () => {
    navigate('/home');
  };

  const prizePatterns: ('early_five' | 'top_line' | 'corners' | 'full_house')[] = [
    'early_five',
    'top_line',
    'corners',
    'full_house'
  ];

  return (
    <div className="relative flex h-screen w-screen flex-col justify-between overflow-hidden">
      {/* 3D background grids */}
      <BackgroundEffects />

      {/* Header bar */}
      <motion.header
        initial={{ y: -20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        className="w-full flex items-center justify-between px-6 py-4 backdrop-blur-md border-b border-white/5 bg-slate-950/20 z-30"
      >
        <button
          onClick={handleBack}
          className="flex items-center space-x-1.5 px-3 py-1.5 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 text-xs font-bold uppercase tracking-wider text-surface-200 hover:text-white transition-colors cursor-pointer"
        >
          <span>&larr;</span>
          <span>Back</span>
        </button>

        <div className="flex flex-col items-center select-none">
          <h2 className="font-display text-base font-black tracking-wider text-white">
            GAME RULES & HELP
          </h2>
          <span className="text-[9px] font-bold tracking-widest text-gradient uppercase">
            Gameplay Manual & Prize Grids
          </span>
        </div>

        {/* Empty status element placeholder */}
        <div className="w-[70px]" />
      </motion.header>

      {/* Main split panels dashboard */}
      <main className="flex-1 grid grid-cols-1 lg:grid-cols-2 gap-8 px-8 py-4 max-h-[calc(100vh-120px)] z-20 overflow-y-auto items-start">
        
        {/* Left Column: Expandable rules accordions & feedback */}
        <div className="space-y-4 h-full flex flex-col justify-between max-h-[300px] lg:max-h-full py-2">
          <div className="space-y-3 overflow-y-auto max-h-[220px] lg:max-h-[340px] pr-1">
            <Accordion title="How to Play">
              Tambola (also known as Housie) is a popular real-time multiplayer game played with numbers 1 to 90.
              Each player is given a ticket consisting of a 3x9 grid containing 15 numbers (5 per row).
              As the game host calls numbers randomly, players match and mark those numbers on their ticket.
              The goal is to claim prize patterns before other competitors.
            </Accordion>

            <Accordion title="Gameplay Flow">
              1. **Buy Tickets**: Buy up to 6 tickets inside waiting rooms.<br />
              2. **Caller Loop**: Once countdown finishes, numbers are called one-by-one every 4.5 seconds.<br />
              3. **Mark Numbers**: Check your tickets and mark numbers as they are called. Enable auto-mark to automate matching.<br />
              4. **Claim Prizes**: Tap claim buttons immediately when matching patterns complete.<br />
              5. **Conclude**: The match completes once a player claims a verified Full House.
            </Accordion>

            <Accordion title="Frequently Asked Questions (FAQ)">
              **Q: Can multiple players claim the same prize?**<br />
              A: Yes. If claims occur on the same called number, the prize is shared equally between the winners.<br /><br />
              **Q: What is Auto Mark?**<br />
              A: Auto Mark automatically matches and marks called numbers on your ticket to avoid missing them.
            </Accordion>
          </div>

          <div className="glass rounded-3xl p-4 border border-white/10 shadow-glow-lg">
            <SupportSection />
          </div>
        </div>

        {/* Right Column: Prize Rules pattern illustrations */}
        <div className="glass rounded-3xl p-5 border border-white/10 shadow-glow-lg flex flex-col justify-start h-full text-left max-h-[300px] lg:max-h-full overflow-hidden">
          <h3 className="font-display text-sm font-black tracking-wider text-amber-300 uppercase mb-4">
            Prize Winning Patterns
          </h3>
          <div className="flex-1 grid grid-cols-2 sm:grid-cols-4 gap-3 items-center justify-center overflow-y-auto">
            {prizePatterns.map((pt) => (
              <PatternExample key={pt} patternType={pt} />
            ))}
          </div>
          <span className="text-[8px] font-mono text-surface-500 mt-4 leading-relaxed text-center">
            *Tap CLAIM buttons immediately when pattern completes. Delayed claims are rejected!
          </span>
        </div>

      </main>
    </div>
  );
}
export default HelpScreen;
